export type IndexComponentRow = {
  exchange: string | null;
  symbol: string;
  weight: string | null;
  price: string | null;
};

function readStringField(row: Record<string, unknown>, keys: string[]): string | null {
  for (const key of keys) {
    const value = row[key];
    if (value == null) continue;
    const text = String(value).trim();
    if (text !== '') return text;
  }
  return null;
}

function parseComponentRow(item: unknown): IndexComponentRow | null {
  if (!item || typeof item !== 'object') return null;
  const row = item as Record<string, unknown>;

  const exchange = readStringField(row, [
    'exch',
    'exchange',
    'exchangeName',
    'venue',
    'source',
  ]);
  const symbol = readStringField(row, [
    'symbol',
    'spotPair',
    'spot_pair',
    'pair',
    'instId',
    'instrument',
    'name',
  ]);

  if (!exchange && !symbol) return null;

  const weightRaw = row.wgt ?? row.weight ?? row.wgtRatio;
  const priceRaw = row.symPx ?? row.price ?? row.lastPrice ?? row.cnvPx;

  return {
    exchange,
    symbol: symbol ?? '—',
    weight: weightRaw != null ? String(weightRaw) : null,
    price: priceRaw != null ? String(priceRaw) : null,
  };
}

export function parseIndexComponentRows(
  payload: Record<string, unknown> | null | undefined,
): IndexComponentRow[] {
  if (!payload) return [];

  const list = payload.components ?? payload.constituents;
  if (!Array.isArray(list)) return [];

  return list
    .map(parseComponentRow)
    .filter((row): row is IndexComponentRow => row != null);
}

export function formatIndexComponentWeight(weight: string | null): string {
  if (weight == null || weight.trim() === '') return '—';
  const n = Number(weight);
  if (!Number.isFinite(n)) return weight;
  if (n > 0 && n <= 1) return `${(n * 100).toFixed(1)}%`;
  return weight;
}

export function indexComponentsSummary(payload: Record<string, unknown> | null | undefined): string | null {
  const rows = parseIndexComponentRows(payload);
  if (rows.length === 0) return null;
  const preview = rows
    .slice(0, 3)
    .map((row) => {
      const label = row.exchange ? `${row.exchange}/${row.symbol}` : row.symbol;
      return row.weight ? `${label} ${formatIndexComponentWeight(row.weight)}` : label;
    })
    .join(', ');
  const suffix = rows.length > 3 ? ` +${rows.length - 3}` : '';
  return `${preview}${suffix}`;
}
